import type { CalcKey } from '../locales/keys';

export interface ApplicationPreset {
  id: string;
  labelKey: CalcKey;
  kind: 'optimization' | 'related-rates';
  /**
   * For `kind: 'optimization'` — the quantity Q(x) to maximise/minimise.
   * Must be parseable by @learnforge/demo-math/expr.
   */
  source?: string;
  /**
   * For `kind: 'related-rates'` — identifies the geometric scenario.
   * `'ladder'`: fixed-length ladder sliding down a wall (base rate → height rate).
   * `'ripple'`: circular ripple on water (radius rate → area rate).
   */
  scenario?: 'ladder' | 'ripple';
  domain: [number, number];
}

export const APPLICATION_PRESETS: ApplicationPreset[] = [
  {
    id: 'open-box',
    labelKey: 'preset.application.open-box',
    kind: 'optimization',
    // Volume of an open box cut from a 10×10 sheet by removing corners of side x:
    // V(x) = x · (10 − 2x)²; maximum at x = 5/3 ≈ 1.667 on (0, 5).
    source: 'x * (10 - 2 * x)^2',
    domain: [0, 5],
  },
  {
    id: 'rect-under-line',
    labelKey: 'preset.application.rect-under-line',
    kind: 'optimization',
    // Rectangle inscribed under the line y = 6 − 2x (first quadrant, x ∈ (0, 3)):
    // A(x) = x · (6 − 2x); maximum area at x = 1.5 (interior of [0, 3]).
    source: 'x * (6 - 2 * x)',
    domain: [0, 3],
  },
  {
    id: 'ladder',
    labelKey: 'preset.application.ladder',
    kind: 'related-rates',
    scenario: 'ladder',
    // A 10-unit ladder leans against a wall; the base slides out at 1 unit/s.
    // x² + y² = 100; dy/dt = −(x/y) · dx/dt. Domain is the base length in (0, 10).
    domain: [0, 10],
  },
  {
    id: 'ripple',
    labelKey: 'preset.application.ripple',
    kind: 'related-rates',
    scenario: 'ripple',
    // A stone dropped in a pond creates a circular ripple; radius grows at 2 units/s.
    // A = π r²; dA/dt = 2πr · dr/dt. Domain is the radius in (0, 8).
    domain: [0, 8],
  },
];
